import { useEffect, useState } from 'react';
import { PaladinLogo } from '../components/shared/PaladinLogo';
import { PixelCrest } from '../components/shared/PixelCrest';
import { useAnimatedMeshParams, type Phase } from './Landing';

const label: React.CSSProperties = {
  fontFamily: '"Silkscreen", monospace',
  fontSize: 12,
  letterSpacing: '0.14em',
  color: '#8AC5D8',
  textAlign: 'center',
  marginTop: 48,
};

/* Same beat sheet as the landing page, with a shorter rest so the cycle repeats quickly */
function usePhaseCycle(period = 14000) {
  const [phase, setPhase] = useState<Phase>('rest');
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const cycle = () => {
      setPhase('damage');
      timers.push(setTimeout(() => setPhase('hold'), 200));
      timers.push(setTimeout(() => setPhase('unwarp'), 1500));
      timers.push(setTimeout(() => setPhase('heal'), 4000));
      timers.push(setTimeout(() => setPhase('fade'), 7800));
      timers.push(setTimeout(() => setPhase('rest'), 10000));
    };
    const first = setTimeout(cycle, 2000);
    let interval: ReturnType<typeof setInterval>;
    const start = setTimeout(() => { interval = setInterval(cycle, period); }, 2000);
    return () => { clearTimeout(first); clearTimeout(start); clearInterval(interval); timers.forEach(clearTimeout); };
  }, [period]);
  return phase;
}

export function CrestPreview() {
  const phase = usePhaseCycle();
  const mesh = useAnimatedMeshParams(phase, { lerpMs: { hold: 1000, unwarp: 1400, heal: 1000 } });
  return (
    <div style={{ minHeight: '100vh', background: '#051A22', display: 'flex', flexWrap: 'wrap', gap: 80, alignItems: 'center', justifyContent: 'center', padding: 48 }}>
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden>
        <filter id="pixelate-crest-preview" x="-15%" y="-15%" width="130%" height="130%">
          <feFlood x="0" y="0" height="1" width="1" />
          <feComposite width="2" height="2" />
          <feTile result="dots" />
          <feComposite in="SourceGraphic" in2="dots" operator="in" />
          <feMorphology operator="dilate" radius="1" />
        </filter>
      </svg>
      <div>
        <div style={{ filter: 'url(#pixelate-crest-preview)' }}>
          <PaladinLogo size={300} shimmer phase={phase} meshColors={mesh.colors} meshSpeed={mesh.speed} meshDistortion={mesh.distortion} meshSwirl={mesh.swirl} pixelArtMode />
        </div>
        <div style={label}>Current</div>
      </div>
      <div>
        <PixelCrest size={300} colors={mesh.colors} speed={mesh.speed} phase={phase} />
        <div style={label}>New — {phase}</div>
      </div>
    </div>
  );
}
